import { Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { attractionAPI } from '../services/api';
import useListQueryParams from '../hooks/useListQueryParams';
import { PageHeader, FilterBar, EmptyState } from '../components/ui';
import './ModuleAdmin.css';

const Attractions = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { query, setQuery } = useListQueryParams({ page: '1', limit: '20', search: '' });
  const page = Number(query.page || 1);
  const limit = Number(query.limit || 20);
  const search = query.search || '';
  const params = { page, limit, search: search || undefined };

  const { data, isLoading } = useQuery(['attractions', page, limit, search], () => attractionAPI.list(params));
  const items = data?.data?.data || [];
  const pagination = data?.data?.pagination || { page: 1, totalPages: 1, total: items.length };

  const deleteMutation = useMutation((id) => attractionAPI.remove(id), {
    onSuccess: () => queryClient.invalidateQueries(['attractions'])
  });

  const handleDelete = (item) => {
    if (!window.confirm(`Delete attraction "${item.name}"?`)) return;
    deleteMutation.mutate(item.id);
  };

  return (
    <div className="module-page">
      <PageHeader
        title="Attractions"
        subtitle="Standalone places (slug, gallery, FAQs) used on event pages when the trek attraction name matches."
        actions={
          <Link to="/attractions/new" className="module-btn">
            Add
          </Link>
        }
      />

      <FilterBar>
        <input
          type="text"
          className="module-filter-input"
          placeholder="Search attractions..."
          value={search}
          onChange={(e) => setQuery({ search: e.target.value })}
        />
      </FilterBar>

      <div className="module-card">
        {isLoading ? (
          <p className="module-loading">Loading attractions...</p>
        ) : items.length === 0 ? (
          <EmptyState
            title="No attractions yet"
            description="Create standalone places used on event pages."
            actionLabel="Add Attraction"
            onAction={() => navigate('/attractions/new')}
          />
        ) : (
          <>
          <table className="module-table">
            <thead>
              <tr>
                <th>Id</th>
                <th>Name</th>
                <th>Slug</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.id}>
                  <td>{item.id?.slice(0, 8) || '–'}</td>
                  <td>{item.name || '–'}</td>
                  <td style={{ fontSize: '0.85rem', wordBreak: 'break-all' }}>{item.slug || '–'}</td>
                  <td className="module-actions">
                    <Link to={`/attractions/${item.id}`} className="module-link-btn">
                      Edit
                    </Link>
                    <button
                      type="button"
                      className="module-link-btn danger"
                      onClick={() => handleDelete(item)}
                      disabled={deleteMutation.isLoading}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {pagination.totalPages > 1 && (
            <div className="module-list-pagination">
              <span>Page {pagination.page} of {pagination.totalPages} ({pagination.total} total)</span>
              <div className="module-list-pagination-btns">
                <button type="button" className="module-btn secondary" disabled={page <= 1} onClick={() => setQuery({ page: String(page - 1) }, { resetPage: false })}>Previous</button>
                <button type="button" className="module-btn secondary" disabled={page >= pagination.totalPages} onClick={() => setQuery({ page: String(page + 1) }, { resetPage: false })}>Next</button>
              </div>
            </div>
          )}
          </>
        )}
      </div>
    </div>
  );
};

export default Attractions;
